// The guided create-benefit flow's step machine.
//
// The flow is a route, not a dialog, so a step is linkable and the browser's back button
// means what it looks like it means. Every piece of state that decides what is on screen
// lives in the query string (`?step=`, `?applicantId=`, `?applicationId=`) rather than in
// component state, which is what makes a half-finished intake survive a reload.

export const CREATE_BENEFIT_STEPS = ["applicant", "applications", "household", "demographics", "income"] as const

export type CreateBenefitStep = (typeof CREATE_BENEFIT_STEPS)[number]

export interface CreateBenefitFlowState {
  applicantId: string | null
  applicationId: string | null
}

export const createBenefitStepLabels: Record<CreateBenefitStep, string> = {
  applicant: "Applicant",
  applications: "Existing Applications",
  household: "Household",
  demographics: "Demographics",
  income: "Income",
}

export function createBenefitStep(value: string | null): CreateBenefitStep {
  return CREATE_BENEFIT_STEPS.includes(value as CreateBenefitStep)
    ? (value as CreateBenefitStep)
    : "applicant"
}

export function stepIndex(step: CreateBenefitStep): number {
  return CREATE_BENEFIT_STEPS.indexOf(step)
}

/**
 * The step the flow can actually show, given what has been decided so far. A deep link to a
 * later step with nothing behind it falls back rather than rendering an empty form: the
 * household and income steps write through the application's own endpoints, so they need a
 * draft to exist first.
 */
export function resolveStep(
  requested: string | null,
  state: CreateBenefitFlowState,
): CreateBenefitStep {
  const step = createBenefitStep(requested)

  if (!state.applicantId) {
    return "applicant"
  }

  if (!state.applicationId && (step === "household" || step === "demographics" || step === "income")) {
    return "applications"
  }

  return step
}

/**
 * What a prior application offers. Editable ones can be resumed in place, which creates
 * nothing; the rest can only be read, so offering "Resume" would promise an edit the server
 * refuses. The Completed state is editable only for staff holding the edit permission,
 * matching the rule the API enforces.
 */
export function priorApplicationAction(
  status: number,
  canEditCompleted: boolean,
): "resume" | "view" {
  const editable = status === 0 || status === 255 || (status === 10 && canEditCompleted)
  return editable ? "resume" : "view"
}
